import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Trash2, Plus, ArrowLeft, MapPin, Crosshair, LoaderCircle, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";
import { ImageUpload, GalleryUpload } from "@/components/ImageUpload";
import HotelMap from "@/components/HotelMap";
import { AMENITY_CATEGORIES, flattenAmenities, bucketLegacyAmenities } from "@/lib/amenitiesCatalog";
import { parseGoogleMapsUrl, isShortMapsUrl } from "@/lib/gmapsParser";

const CONCEPTS = ["Ultra Her Şey Dahil", "Her Şey Dahil", "Alkolsüz Her Şey Dahil", "Yarım Pansiyon", "Tam Pansiyon", "Oda Kahvaltı", "Sadece Oda"];
const AMENITIES_POOL = [
    "Ücretsiz Wi-Fi", "Açık Havuz", "Kapalı Havuz", "Spa & Wellness", "Fitness Merkezi",
    "Özel Plaj", "Aquapark", "Çocuk Kulübü", "Tenis Kortu", "Golf Sahası", "Restoran", "Bar",
    "24 Saat Resepsiyon", "Ücretsiz Otopark", "Klima", "Mini Bar", "Oda Servisi", "Çamaşırhane",
    "Konferans Salonu", "Hamam", "Sauna", "Animasyon", "Havalimanı Transferi"
];
const ROOM_FEATURES = ["Klima", "Mini Bar", "LCD TV", "Wi-Fi", "Banyo", "Balkon", "Saç Kurutma Makinesi", "Kasa", "Telefon"];

const EMPTY_HOTEL = {
    name: "", destination_slug: "", region: "", stars: 4, rating: 8.5, review_count: 0,
    concept: "Her Şey Dahil", description: "",
    main_image: "", gallery: [],
    amenities: [], amenities_categorized: {}, address: "", email: "", phone: "", latitude: 0, longitude: 0,
    beach_distance_m: 0, airport_distance_km: 0,
    tags: [], is_featured: false, is_deal: false,
    pricing_type: "commission", commission_percent: 12,
    room_types: [],
};

const EMPTY_ROOM = {
    name: "Standart Oda", capacity: 2, max_children: 1, size_sqm: 28, bed_type: "1 Double yatak",
    image: "",
    available_count: 5, features: ["Klima", "Mini Bar", "LCD TV", "Wi-Fi"],
    price_per_night: 0, extra_person_fee: 0, child_discount_percent: 0, pricing_rules: [],
};

export default function AdminHotelFormPage({
    apiClient,
    Layout = AdminLayout,
    listRoute = "/admin/hotels",
    ratesRoute,
    skipAuthCheck = false,
}) {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth() || {};
    const client = apiClient || api;
    const goRates = ratesRoute || ((hid) => `/admin/hotels/${hid}/rates`);
    const [destinations, setDestinations] = useState([]);
    const [hotel, setHotel] = useState(EMPTY_HOTEL);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        // For partner panel, skipAuthCheck=true (partner's own route guard handles it)
        if (!skipAuthCheck) {
            if (authLoading) return;
            if (!user || !["super_admin", "admin", "otel_owner", "otel_manager"].includes(user.role)) return navigate("/login");
        }
        // destinations is a public endpoint — works for both master & partner JWT
        client.get("/destinations").then((r) => setDestinations(r.data)).catch(() => setDestinations([]));
        if (isEdit) {
            client.get(`/admin/hotels/${id}`)
                .then((r) => setHotel(r.data))
                .catch(() => setError("Otel yüklenemedi"))
                .finally(() => setLoading(false));
        }
    }, [user, authLoading, id, skipAuthCheck]); // eslint-disable-line

    const update = (k, v) => setHotel((h) => ({ ...h, [k]: v }));

    // Categorized amenities
    const [activeCat, setActiveCat] = useState(AMENITY_CATEGORIES[0].key);
    const ensureCat = (h) => {
        let cat = h.amenities_categorized;
        if (!cat || !Object.keys(cat).length) {
            cat = bucketLegacyAmenities(h.amenities || []);
        }
        AMENITY_CATEGORIES.forEach((c) => { if (!cat[c.key]) cat[c.key] = []; });
        return cat;
    };
    const toggleCatAmenity = (catKey, item) => setHotel((h) => {
        const cat = ensureCat(h);
        const cur = cat[catKey] || [];
        const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
        const newCat = { ...cat, [catKey]: next };
        return { ...h, amenities_categorized: newCat, amenities: flattenAmenities(newCat) };
    });

    // Google Maps location paste
    const [gmapsUrl, setGmapsUrl] = useState("");
    const [resolving, setResolving] = useState(false);
    const handleGmapsPaste = async () => {
        if (!gmapsUrl.trim()) return;
        // Try client-side parse first
        const parsed = parseGoogleMapsUrl(gmapsUrl);
        if (parsed) {
            update("latitude", parsed.lat);
            update("longitude", parsed.lng);
            toast.success(`Konum bulundu: ${parsed.lat.toFixed(5)}, ${parsed.lng.toFixed(5)}`);
            setGmapsUrl("");
            return;
        }
        // Shortened — resolve via backend
        if (isShortMapsUrl(gmapsUrl)) {
            setResolving(true);
            try {
                const { data } = await client.get(`/geo/resolve-gmaps?url=${encodeURIComponent(gmapsUrl)}`);
                if (data.lat && data.lng) {
                    update("latitude", data.lat);
                    update("longitude", data.lng);
                    toast.success(`Konum bulundu: ${data.lat.toFixed(5)}, ${data.lng.toFixed(5)}`);
                    setGmapsUrl("");
                } else {
                    toast.error("URL çözüldü ama konum bulunamadı. Lütfen Google Maps'ten 'Bu konumu paylaş' ile tam adresi kopyalayın.");
                }
            } catch (e) {
                toast.error("Kısa URL çözülemedi");
            } finally {
                setResolving(false);
            }
            return;
        }
        toast.error("Geçersiz format. Şu örnekleri deneyin: https://www.google.com/maps/@36.7666,31.3893,15z VEYA 36.7666, 31.3893");
    };

    const toggleAmenity = (a) => setHotel((h) => ({ ...h, amenities: h.amenities.includes(a) ? h.amenities.filter((x) => x !== a) : [...h.amenities, a] }));
    const addRoom = () => update("room_types", [...(hotel.room_types || []), { ...EMPTY_ROOM, id: undefined }]);
    const removeRoom = (idx) => update("room_types", hotel.room_types.filter((_, i) => i !== idx));
    const updateRoom = (idx, k, v) => update("room_types", hotel.room_types.map((r, i) => i === idx ? { ...r, [k]: v } : r));
    const toggleRoomFeature = (idx, f) => {
        const r = hotel.room_types[idx];
        updateRoom(idx, "features", r.features.includes(f) ? r.features.filter((x) => x !== f) : [...r.features, f]);
    };

    const onSave = async () => {
        setError("");
        if (!hotel.name || !hotel.destination_slug || !hotel.main_image) {
            setError("Ad, şehir ve ana görsel zorunludur"); return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                const { room_types, slug, id: hid, destination_name, currency, created_at, ...rest } = hotel;
                await client.patch(`/admin/hotels/${id}`, rest);
                const latest = await client.get(`/admin/hotels/${id}`).then((r) => r.data);
                const existingIds = new Set(latest.room_types.map((r) => r.id));
                const formIds = new Set((hotel.room_types || []).filter((r) => r.id).map((r) => r.id));
                for (const rid of existingIds) {
                    if (!formIds.has(rid)) await client.delete(`/admin/hotels/${id}/rooms/${rid}`);
                }
                for (const r of hotel.room_types) {
                    const payload = { ...r };
                    delete payload.id;
                    if (r.id && existingIds.has(r.id)) {
                        await client.patch(`/admin/hotels/${id}/rooms/${r.id}`, payload);
                    } else {
                        await client.post(`/admin/hotels/${id}/rooms`, payload);
                    }
                }
            } else {
                await client.post("/admin/hotels", hotel);
            }
            navigate(listRoute);
        } catch (e) {
            setError(e.response?.data?.detail || "Kaydetme başarısız");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Layout title="Yükleniyor..."><div>Yükleniyor...</div></Layout>;

    return (
        <Layout
            title={isEdit ? "Otel Düzenle" : "Yeni Otel"}
            action={
                <div className="flex items-center gap-2">
                    <button onClick={() => navigate(listRoute)} className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-medium text-slate-700 dark:text-slate-200">
                        <ArrowLeft className="h-4 w-4 inline -mt-0.5" /> Geri
                    </button>
                    <button data-testid="save-hotel-btn" onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold disabled:opacity-60">
                        <Save className="h-4 w-4" /> {saving ? "Kaydediliyor..." : "Kaydet"}
                    </button>
                </div>
            }
        >
            {error && <div data-testid="form-error" className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

            <div className="space-y-6">
                <Section title="Temel Bilgiler">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Otel Adı *"><input data-testid="hotel-name" value={hotel.name} onChange={(e) => update("name", e.target.value)} className={INPUT} /></Field>
                        <Field label="Şehir *">
                            <select data-testid="hotel-destination" value={hotel.destination_slug} onChange={(e) => update("destination_slug", e.target.value)} className={INPUT}>
                                <option value="">Seçin...</option>
                                {destinations.map((d) => <option key={d.id || d.slug} value={d.slug}>{d.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Bölge"><input value={hotel.region || ""} onChange={(e) => update("region", e.target.value)} className={INPUT} placeholder="Akdeniz / Ege ..." /></Field>
                        <Field label="Adres"><input value={hotel.address || ""} onChange={(e) => update("address", e.target.value)} className={INPUT} /></Field>
                        <Field label="Otel E-posta (Voucher gönderimi için)"><input data-testid="hotel-email" type="email" placeholder="rezervasyon@otelornek.com" value={hotel.email || ""} onChange={(e) => update("email", e.target.value)} className={INPUT} /></Field>
                        <Field label="Otel Telefonu"><input data-testid="hotel-phone" value={hotel.phone || ""} onChange={(e) => update("phone", e.target.value)} className={INPUT} /></Field>
                        <Field label="Yıldız (3-5)"><input type="number" min="1" max="5" value={hotel.stars} onChange={(e) => update("stars", Number(e.target.value))} className={INPUT} /></Field>
                        <Field label="Puan (0-10)"><input type="number" min="0" max="10" step="0.1" value={hotel.rating} onChange={(e) => update("rating", Number(e.target.value))} className={INPUT} /></Field>
                        <Field label="Yorum Sayısı"><input type="number" min="0" value={hotel.review_count} onChange={(e) => update("review_count", Number(e.target.value))} className={INPUT} /></Field>
                        <Field label="Plaja Uzaklık (m)"><input type="number" min="0" value={hotel.beach_distance_m} onChange={(e) => update("beach_distance_m", Number(e.target.value))} className={INPUT} /></Field>
                        <Field label="Havalimanına Uzaklık (km)"><input type="number" min="0" value={hotel.airport_distance_km} onChange={(e) => update("airport_distance_km", Number(e.target.value))} className={INPUT} /></Field>
                    </div>

                    {/* Pricing type: Net vs Commission */}
                    <div className="mt-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Fiyat Tipi</div>
                        <div className="grid grid-cols-2 gap-3">
                            <label
                                data-testid="pricing-type-commission"
                                className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${hotel.pricing_type === "commission" ? "bg-sky-50 dark:bg-sky-950/30 border-sky-500" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-slate-300"}`}
                            >
                                <input type="radio" name="pricing_type" checked={hotel.pricing_type === "commission"} onChange={() => update("pricing_type", "commission")} className="mt-0.5 h-4 w-4 accent-sky-500" />
                                <div className="flex-1 min-w-0">
                                    <div className={`text-sm font-bold ${hotel.pricing_type === "commission" ? "text-sky-900 dark:text-sky-100" : "text-slate-900 dark:text-white"}`}>Komisyonlu</div>
                                    <div className="text-xs text-slate-500 mt-0.5">Otel fiyatına bizim komisyonumuz eklenir</div>
                                </div>
                            </label>
                            <label
                                data-testid="pricing-type-net"
                                className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${hotel.pricing_type === "net" ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-500" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-slate-300"}`}
                            >
                                <input type="radio" name="pricing_type" checked={hotel.pricing_type === "net"} onChange={() => update("pricing_type", "net")} className="mt-0.5 h-4 w-4 accent-emerald-500" />
                                <div className="flex-1 min-w-0">
                                    <div className={`text-sm font-bold ${hotel.pricing_type === "net" ? "text-emerald-900 dark:text-emerald-100" : "text-slate-900 dark:text-white"}`}>Net</div>
                                    <div className="text-xs text-slate-500 mt-0.5">Otel fiyatı doğrudan müşteriye yansır</div>
                                </div>
                            </label>
                        </div>
                        {hotel.pricing_type === "commission" && (
                            <div className="mt-3">
                                <Field label="Komisyon Oranı (%)">
                                    <input data-testid="hotel-commission" type="number" min="0" max="100" step="0.5" value={hotel.commission_percent ?? 12} onChange={(e) => update("commission_percent", Number(e.target.value))} className={INPUT} />
                                </Field>
                            </div>
                        )}
                    </div>
                    <Field label="Açıklama" className="mt-4">
                        <textarea rows={4} value={hotel.description} onChange={(e) => update("description", e.target.value)} className={INPUT} />
                    </Field>
                    <div className="flex items-center gap-6 mt-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={hotel.is_featured} onChange={(e) => update("is_featured", e.target.checked)} className="h-4 w-4 accent-sky-500" />
                            <span className="text-sm text-slate-700 dark:text-slate-200">Öne Çıkan</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={hotel.is_deal} onChange={(e) => update("is_deal", e.target.checked)} className="h-4 w-4 accent-amber-500" />
                            <span className="text-sm text-slate-700 dark:text-slate-200">Fırsat / Kampanya</span>
                        </label>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">💡 Fiyat ve iptal politikası <strong>Fiyat Planları</strong> sayfasından yönetilir.</p>
                </Section>

                <Section title="Görseller">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Ana Görsel *</label>
                            <ImageUpload testid="hotel-main-upload" value={hotel.main_image} onChange={(url) => update("main_image", url)} height="h-56" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Galeri ({hotel.gallery?.length || 0})</label>
                            <GalleryUpload testid="hotel-gallery-upload" value={hotel.gallery || []} onChange={(v) => update("gallery", v)} />
                        </div>
                    </div>
                </Section>

                <Section title="Tesis Özellikleri (Kategorize)">
                    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-3">
                        {/* Category sidebar */}
                        <aside className="space-y-1" data-testid="ah-am-cats">
                            {AMENITY_CATEGORIES.map((cat) => {
                                const count = (ensureCat(hotel)[cat.key] || []).length;
                                const isActive = activeCat === cat.key;
                                return (
                                    <button
                                        key={cat.key}
                                        type="button"
                                        onClick={() => setActiveCat(cat.key)}
                                        data-testid={`ah-cat-${cat.key}`}
                                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${isActive
                                            ? `bg-${cat.color}-500 text-white`
                                            : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                                    >
                                        <span className="truncate">{cat.label}</span>
                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${isActive ? "bg-white/25 text-white" : `bg-${cat.color}-100 dark:bg-${cat.color}-950/40 text-${cat.color}-700 dark:text-${cat.color}-300`}`}>
                                            {count}/{cat.items.length}
                                        </span>
                                    </button>
                                );
                            })}
                        </aside>
                        {/* Items */}
                        <div>
                            {(() => {
                                const ac = AMENITY_CATEGORIES.find((c) => c.key === activeCat) || AMENITY_CATEGORIES[0];
                                const cat = ensureCat(hotel);
                                const selected = cat[ac.key] || [];
                                return (
                                    <>
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className={`text-sm font-bold text-${ac.color}-600 dark:text-${ac.color}-400`}>{ac.label} ({selected.length}/{ac.items.length})</h4>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const allOn = selected.length === ac.items.length;
                                                    const next = allOn ? [] : [...ac.items];
                                                    setHotel((h) => {
                                                        const c = ensureCat(h);
                                                        const nc = { ...c, [ac.key]: next };
                                                        return { ...h, amenities_categorized: nc, amenities: flattenAmenities(nc) };
                                                    });
                                                }}
                                                className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700"
                                            >
                                                {selected.length === ac.items.length ? "Tümünü Kaldır" : "Tümünü Seç"}
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                                            {ac.items.map((it) => {
                                                const checked = selected.includes(it);
                                                return (
                                                    <button
                                                        key={it}
                                                        type="button"
                                                        onClick={() => toggleCatAmenity(ac.key, it)}
                                                        data-testid={`ah-amenity-${ac.key}-${it.replace(/\s+/g, '-').toLowerCase()}`}
                                                        className={`text-sm text-left px-3 py-2 rounded-xl border transition-all ${checked
                                                            ? `bg-${ac.color}-50 dark:bg-${ac.color}-950/30 border-${ac.color}-500 text-${ac.color}-800 dark:text-${ac.color}-200 font-semibold`
                                                            : "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-slate-400"}`}
                                                    >
                                                        {checked ? "✓ " : ""}{it}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </Section>

                <Section title="Konum (Harita)">
                    <div className="space-y-3">
                        <div className="p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900">
                            <label className="block text-xs font-bold uppercase tracking-widest text-sky-700 dark:text-sky-300 mb-2 flex items-center gap-1.5">
                                <LinkIcon className="h-3.5 w-3.5" /> Google Maps Bağlantısı Yapıştır
                            </label>
                            <div className="flex gap-2 flex-wrap">
                                <input
                                    type="text"
                                    value={gmapsUrl}
                                    onChange={(e) => setGmapsUrl(e.target.value)}
                                    placeholder="https://www.google.com/maps/@36.7666,31.3893,15z  veya  36.7666, 31.3893"
                                    className="flex-1 min-w-[280px] px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-sm font-mono"
                                    data-testid="ah-gmaps-input"
                                />
                                <button
                                    type="button"
                                    onClick={handleGmapsPaste}
                                    disabled={resolving || !gmapsUrl.trim()}
                                    data-testid="ah-gmaps-apply"
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 disabled:bg-slate-400 text-white text-sm font-bold"
                                >
                                    {resolving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
                                    Konumu Bul
                                </button>
                            </div>
                            <p className="text-[11px] text-sky-700 dark:text-sky-300 mt-2">
                                💡 Google Maps&apos;te otele sağ tık → koordinatlara tıklayın (kopyalanır) → yukarı yapıştırın.
                                Veya Google Maps URL&apos;sini olduğu gibi yapıştırabilirsiniz (maps.app.goo.gl/... kısaltma URL&apos;leri de çalışır).
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Enlem (Latitude)</label>
                                <input
                                    type="number"
                                    step="any"
                                    value={hotel.latitude || ""}
                                    onChange={(e) => update("latitude", parseFloat(e.target.value) || 0)}
                                    className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-sm"
                                    data-testid="ah-lat"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Boylam (Longitude)</label>
                                <input
                                    type="number"
                                    step="any"
                                    value={hotel.longitude || ""}
                                    onChange={(e) => update("longitude", parseFloat(e.target.value) || 0)}
                                    className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-sm"
                                    data-testid="ah-lng"
                                />
                            </div>
                        </div>

                        {hotel.latitude && hotel.longitude ? (
                            <div data-testid="ah-map-preview">
                                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                                    <MapPin className="h-3.5 w-3.5 text-emerald-500" />
                                    <span><strong>Konum işaretlendi:</strong> {Number(hotel.latitude).toFixed(5)}, {Number(hotel.longitude).toFixed(5)}</span>
                                    <button
                                        type="button"
                                        onClick={() => { update("latitude", 0); update("longitude", 0); }}
                                        className="text-rose-500 hover:underline"
                                    >Temizle</button>
                                </div>
                                <HotelMap lat={Number(hotel.latitude)} lng={Number(hotel.longitude)} name={hotel.name || "Otel"} height={240} />
                            </div>
                        ) : (
                            <div className="p-4 text-center text-sm text-slate-500 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-dashed border-slate-300 dark:border-slate-700">
                                Henüz konum işaretlenmemiş. Google Maps bağlantısı yapıştırarak veya enlem/boylam&apos;ı manuel girerek başlayın.
                            </div>
                        )}
                    </div>
                </Section>

                {/* Legacy flat-list amenities hidden; keeping function for compat */}
                {false && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {AMENITIES_POOL.map((a) => (
                            <label key={a} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                                <input type="checkbox" checked={hotel.amenities.includes(a)} onChange={() => toggleAmenity(a)} className="h-4 w-4 accent-sky-500" />
                                <span className="text-sm text-slate-700 dark:text-slate-200">{a}</span>
                            </label>
                        ))}
                    </div>
                )}

                <Section title={`Oda Tipleri (${hotel.room_types?.length || 0})`} action={<button data-testid="add-room-btn" onClick={addRoom} className="inline-flex items-center gap-1 text-sm font-semibold text-sky-500"><Plus className="h-4 w-4" /> Oda Tipi Ekle</button>}>
                    <div className="space-y-4">
                        {(hotel.room_types || []).map((r, rIdx) => (
                            <div key={rIdx} data-testid={`room-edit-${rIdx}`} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-semibold text-slate-900 dark:text-white">Oda #{rIdx + 1}: {r.name}</h4>
                                    <button onClick={() => removeRoom(rIdx)} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"><Trash2 className="h-4 w-4" /></button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Oda Görseli</label>
                                        <ImageUpload testid={`room-image-${rIdx}`} value={r.image} onChange={(url) => updateRoom(rIdx, "image", url)} height="h-40" />
                                    </div>
                                    <div className="md:col-span-2 grid grid-cols-2 gap-3">
                                        <Field label="Oda Adı"><input value={r.name} onChange={(e) => updateRoom(rIdx, "name", e.target.value)} className={INPUT} /></Field>
                                        <Field label="Kapasite (toplam kişi)"><input data-testid={`room-capacity-${rIdx}`} type="number" min="1" max="10" value={r.capacity} onChange={(e) => updateRoom(rIdx, "capacity", Number(e.target.value))} className={INPUT} /></Field>
                                        <Field label="Max Çocuk Sayısı"><input data-testid={`room-max-children-${rIdx}`} type="number" min="0" max={Math.max(0, (r.capacity || 1) - 1)} value={r.max_children ?? 0} onChange={(e) => updateRoom(rIdx, "max_children", Number(e.target.value))} className={INPUT} /></Field>
                                        <Field label="m²"><input type="number" min="0" value={r.size_sqm} onChange={(e) => updateRoom(rIdx, "size_sqm", Number(e.target.value))} className={INPUT} /></Field>
                                        <Field label="Yatak Tipi"><input value={r.bed_type} onChange={(e) => updateRoom(rIdx, "bed_type", e.target.value)} className={INPUT} /></Field>
                                        <Field label="Stoktaki Oda Sayısı (varsayılan kontenjan)" className="col-span-2"><input type="number" min="0" value={r.available_count} onChange={(e) => updateRoom(rIdx, "available_count", Number(e.target.value))} className={INPUT} /></Field>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Oda Özellikleri</div>
                                    <div className="flex flex-wrap gap-2">
                                        {ROOM_FEATURES.map((f) => (
                                            <label key={f} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 cursor-pointer text-sm">
                                                <input type="checkbox" checked={r.features.includes(f)} onChange={() => toggleRoomFeature(rIdx, f)} className="h-3 w-3 accent-sky-500" />
                                                <span className="text-slate-700 dark:text-slate-200">{f}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {(!hotel.room_types || hotel.room_types.length === 0) && (
                            <p className="text-sm text-slate-500 text-center py-6">Henüz oda tipi yok. &quot;Oda Tipi Ekle&quot; ile başlayın.</p>
                        )}
                    </div>

                    {/* Fiyat Planı CTA */}
                    {hotel.room_types?.length > 0 && (
                        <div className="mt-5 p-4 rounded-xl bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-sky-950/30 dark:to-indigo-950/30 border border-sky-200 dark:border-sky-800 flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-[200px]">
                                <div className="text-sm font-bold text-sky-900 dark:text-sky-100">Bu odalar için Fiyat Planı oluşturmak ister misiniz?</div>
                                <div className="text-xs text-sky-700 dark:text-sky-300 mt-0.5">Fiyat planı: pansiyon tipi, iptal politikası, 1/2/3 kişi fiyatları ve takvim yönetimi.</div>
                            </div>
                            {isEdit ? (
                                <button
                                    data-testid="create-rate-plan-btn"
                                    onClick={() => navigate(goRates(id))}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold"
                                >
                                    <Plus className="h-4 w-4" /> Fiyat Planı Oluştur
                                </button>
                            ) : (
                                <button
                                    data-testid="save-and-go-rates-btn"
                                    onClick={async () => {
                                        if (!hotel.name || !hotel.destination_slug || !hotel.main_image) {
                                            setError("Önce Ad, destinasyon ve ana görsel girin"); return;
                                        }
                                        setSaving(true);
                                        try {
                                            const created = await client.post("/admin/hotels", hotel).then((r) => r.data);
                                            navigate(goRates(created.id));
                                        } catch (e) {
                                            setError(e.response?.data?.detail || "Kaydetme başarısız");
                                        } finally {
                                            setSaving(false);
                                        }
                                    }}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold disabled:opacity-60"
                                    disabled={saving}
                                >
                                    <Save className="h-4 w-4" /> Kaydet ve Fiyat Planı Oluştur
                                </button>
                            )}
                        </div>
                    )}
                </Section>
            </div>
        </Layout>
    );
}

const INPUT = "w-full px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-white";

function Section({ title, children, action }) {
    return (
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">{title}</h2>
                {action}
            </div>
            {children}
        </div>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>
            {children}
        </label>
    );
}
